import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Books, MagnifyingGlass, ArrowLeft, Buildings, CaretRight, FileText,
} from "@phosphor-icons/react";
import RepositorioTab from "@/components/aseguradora/RepositorioTab";

/** Página dedicada al Repositorio Documental.
 *
 * Rutas:
 * - /repositorio                  → buscador global + lista de aseguradoras
 * - /repositorio/:aseguradoraId   → repositorio de una aseguradora concreta
 *
 * El componente reutiliza `RepositorioTab` (mismo que se muestra dentro de
 * /admin/aseguradoras/:id) para que la UX sea consistente.
 */
export default function Repositorio() {
  const { aseguradoraId } = useParams();
  const navigate = useNavigate();

  const [aseguradoras, setAseguradoras] = useState([]);
  const [selected, setSelected] = useState(null);
  const [globalQ, setGlobalQ] = useState("");
  const [globalRes, setGlobalRes] = useState(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/aseguradoras");
        const items = Array.isArray(data) ? data : (data.items || []);
        setAseguradoras(items);
        if (aseguradoraId) {
          setSelected(items.find((a) => a.id === aseguradoraId) || null);
        }
      } catch (e) { toast.error("No se pudo cargar la lista de aseguradoras"); }
    })();
  }, [aseguradoraId]);

  const runGlobal = async (e) => {
    e?.preventDefault?.();
    if (globalQ.trim().length < 2) return;
    setSearching(true);
    try {
      const { data } = await api.get(`/repositorio/buscar-global?q=${encodeURIComponent(globalQ)}&top=25`);
      setGlobalRes(data);
    } catch (e) { toast.error("Error en búsqueda global"); } finally { setSearching(false); }
  };

  if (aseguradoraId && selected) {
    return (
      <div className="px-6 py-4 space-y-4" data-testid="repositorio-page">
        <div className="flex items-center gap-2 text-sm">
          <button onClick={() => navigate("/repositorio")} className="text-muted hover:text-foreground inline-flex items-center gap-1" data-testid="repo-back">
            <ArrowLeft className="w-3.5 h-3.5" weight="bold" /> Repositorio global
          </button>
          <CaretRight className="w-3 h-3 text-muted" weight="bold" />
          <span className="font-semibold inline-flex items-center gap-1.5">
            <Buildings className="w-4 h-4 text-amber-700" weight="fill" />
            {selected.name}
          </span>
        </div>
        <RepositorioTab
          aseguradoraId={selected.id}
          aseguradoraName={selected.name}
          canEdit={true}
        />
      </div>
    );
  }

  return (
    <div className="px-6 py-6 max-w-6xl mx-auto space-y-6" data-testid="repositorio-global-page">
      <header className="space-y-1">
        <h1 className="font-display font-bold text-2xl tracking-tight">Repositorio Documental</h1>
        <p className="text-sm text-muted">
          Consulta y administra circulares, pólizas, instrucciones técnicas y baremos de todas las aseguradoras.
        </p>
      </header>

      {/* Búsqueda global */}
      <form onSubmit={runGlobal} className="bg-white border border-border rounded-md p-3">
        <div className="text-[10px] uppercase tracking-widest font-bold text-amber-700 mb-1.5">
          Búsqueda cross-aseguradora
        </div>
        <div className="flex gap-2">
          <input
            value={globalQ}
            onChange={(e) => setGlobalQ(e.target.value)}
            placeholder="Busca en TODAS las aseguradoras (título / nombre / resumen)…"
            data-testid="repo-global-search-input"
            className="flex-1 text-[13px]"
          />
          <button type="submit" disabled={searching || globalQ.length < 2} className="btn-brand text-[12px] px-3 py-1.5 rounded-md font-semibold inline-flex items-center gap-1 disabled:opacity-50" data-testid="repo-global-search-submit">
            <MagnifyingGlass className="w-3.5 h-3.5" weight="bold" /> {searching ? "Buscando…" : "Buscar"}
          </button>
        </div>
      </form>

      {globalRes && (
        <div className="space-y-2">
          <div className="text-[10px] uppercase tracking-widest font-bold text-muted">
            Resultados ({globalRes.count})
          </div>
          {globalRes.results.length === 0 ? (
            <div className="text-sm text-muted italic">Ningún documento coincide.</div>
          ) : (
            <div className="space-y-2">
              {globalRes.results.map((d) => (
                <Link
                  key={d.id}
                  to={`/repositorio/${d.aseguradora_id}`}
                  data-testid={`repo-global-result-${d.id}`}
                  className="block bg-white border border-border rounded-md p-3 hover:border-amber-400 transition-colors"
                >
                  <div className="flex items-start gap-2">
                    <FileText className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" weight="fill" />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-[13px] truncate">{d.title}</div>
                      <div className="text-[11px] text-muted truncate">
                        <Buildings className="w-3 h-3 inline mr-1" weight="fill" />
                        {d.aseguradora_name} · {d.filename}
                      </div>
                      {d.summary && (
                        <div className="text-[11.5px] text-muted mt-1 line-clamp-2">{d.summary}</div>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Lista de aseguradoras */}
      <div>
        <h2 className="text-[10px] uppercase tracking-widest font-bold text-muted mb-2">
          Aseguradoras ({aseguradoras.length})
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
          {aseguradoras.map((a) => (
            <Link
              key={a.id}
              to={`/repositorio/${a.id}`}
              data-testid={`repo-aseg-card-${a.id}`}
              className="bg-white border border-border rounded-md p-3 hover:border-amber-400 hover:shadow-sm transition-all inline-flex items-center gap-2.5"
            >
              <Books className="w-5 h-5 text-amber-700 flex-shrink-0" weight="fill" />
              <div className="min-w-0">
                <div className="font-semibold text-[13px] truncate">{a.name}</div>
                <div className="text-[10.5px] text-muted truncate">{a.code || a.cif || "—"}</div>
              </div>
              <CaretRight className="w-3.5 h-3.5 text-muted ml-auto" weight="bold" />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
